import {WriteButton, WriteIconButton} from '../../common/buttons/WriteActions';
import {ArrowBackIosNew, Search} from '@mui/icons-material';
import {Alert, Box, Button, Card, CardContent, Checkbox, Chip, Divider, FormControl, FormControlLabel, InputAdornment, InputLabel, MenuItem, Select, Stack, TextField, Typography} from '@mui/material';
import {Add, DeleteOutline} from '@mui/icons-material';
import {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {RECIPE_SIZE} from '@mybake/ui/common/dictionary';
import {recipeAPI} from '../../../services/RecipeServices';
import {productTypesAPI} from '../../../services/ProductTypeServices';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {getApiErrorMessage, getApiFieldErrors} from '../../../common/apiErrors';
import {ContentSkeleton} from '../../common/loading/ContentSkeleton';
import {SaveProductTypePriceTierGuidRequest} from '../../../models/ProductTypes/IProductTypeGuid';
import {MoneyField} from '../../common/inputs/MoneyField';
import {toSaveProductTypeGuidRequest} from '../../../mappers/productTypeGuid.mapper';
import {getRecipeProductTypeAssignments} from './recipeProductTypeAssignments';

export const ProductTypeDetailPage = () => {
    const {t} = useAppTranslation();
    const {id = ''} = useParams();
    const navigate = useNavigate();
    const {data: productType, error: loadError, isLoading: isTypeLoading} = productTypesAPI.useGetProductTypeV2Query(id, {skip: !id});
    const {data: productTypes, isLoading: areProductTypesLoading} = productTypesAPI.useGetProductTypesV2Query();
    const {data: recipes, isLoading: areRecipesLoading} = recipeAPI.useGetRecipesV2AllLightQuery();
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [search, setSearch] = useState('');
    const [requestError, setRequestError] = useState<unknown>();
    const [priceTiers, setPriceTiers] = useState<SaveProductTypePriceTierGuidRequest[]>([]);
    const [updateProductType, {isLoading: isSaving}] = productTypesAPI.useUpdateProductTypeV2Mutation();

    useEffect(() => {
        if (productType) {
            setSelectedIds(new Set((productType.recipes || []).map((recipe) => recipe.id)));
            setPriceTiers(productType.priceTiers ?? []);
        }
    }, [productType]);

    const updateTier = (index: number, patch: Partial<SaveProductTypePriceTierGuidRequest>) => setPriceTiers(current => current.map((tier, tierIndex) => tierIndex === index ? {...tier, ...patch} : tier));
    const removeTier = (index: number) => setPriceTiers(current => current.filter((_tier, tierIndex) => tierIndex !== index));
    const activeTiers = priceTiers;
    const invalidTiers = activeTiers.some(tier => !Number.isFinite(Number(tier.fromValue)) || Number(tier.fromValue) < 0 || !Number.isFinite(Number(tier.price)) || Number(tier.price) < 0)
        || new Set(activeTiers.map(tier => Number(tier.fromValue))).size !== activeTiers.length;

    const filteredRecipes = useMemo(() => (recipes || []).filter((recipe) =>
        recipe.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase())
    ), [recipes, search]);
    const recipeAssignments = useMemo(() => getRecipeProductTypeAssignments(productTypes ?? []), [productTypes]);

    const toggleRecipe = (recipeId: string) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            if (next.has(recipeId)) next.delete(recipeId);
            else next.add(recipeId);
            return next;
        });
    };

    const save = async () => {
        if (!productType) return;
        const selectedRecipes = (recipes || []).filter((recipe) => selectedIds.has(recipe.id));

        const updated = {
            ...productType,
            recipes: selectedRecipes,
            // This screen only manages recipes; retain existing package relations.
            packages: productType.packages || [],
            priceTiers,
        };
        try { await updateProductType({id: productType.id, body: toSaveProductTypeGuidRequest(updated, true)}).unwrap();
        navigate('..', {relative: 'path'});
        } catch (error) { setRequestError(error); }
    };

    if (isTypeLoading || areProductTypesLoading || areRecipesLoading) return <ContentSkeleton variant="form"/>;
    if (loadError) return <Alert severity="error">{getApiErrorMessage(loadError, t)}</Alert>;
    if (!productType) return <Box sx={{p: 3}}><Typography>{t('recipes:product_type_not_found')}</Typography></Box>;

    return (
        <Box sx={{maxWidth: 760, mx: 'auto', p: {xs: 2, sm: 3}}}>
            <Button startIcon={<ArrowBackIosNew/>} onClick={() => navigate('..', {relative: 'path'})}>{t('recipes:back_to_product_types')}</Button>
            <Typography variant="h4" sx={{mt: 2}}>{productType.name}</Typography>
            <Typography color="text.secondary" sx={{mb: 3}}>{t('recipes:product_type_recipes_description')}</Typography>
            {Boolean(requestError) && <Alert severity="error" sx={{mb: 2}}>{getApiFieldErrors(requestError, t).recipes || getApiFieldErrors(requestError, t).packages || getApiErrorMessage(requestError, t)}</Alert>}
            <TextField
                fullWidth
                label={t('recipes:search_recipes')}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                InputProps={{startAdornment: <InputAdornment position="start"><Search/></InputAdornment>}}
                sx={{mb: 2}}
            />
            <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>{t('recipes:selected_count', {count: selectedIds.size})}</Typography>
            <Box sx={{border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: '55vh', overflow: 'auto', px: 1}}>
                {filteredRecipes.map((recipe, index) => {
                    const assignedProductType = recipeAssignments.get(recipe.id);
                    return <Box key={recipe.id}>
                        <FormControlLabel
                            sx={{width: '100%', minHeight: 48, m: 0}}
                            control={<Checkbox checked={selectedIds.has(recipe.id)} onChange={() => toggleRecipe(recipe.id)}/>}
                            label={
                                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{width: '100%', minWidth: 0}}>
                                    <Typography noWrap>{recipe.name}</Typography>
                                    <Chip
                                        size="small"
                                        color={!assignedProductType ? 'warning' : assignedProductType.id === productType.id ? 'primary' : 'default'}
                                        label={assignedProductType?.name ?? t('recipes:no_product_type')}
                                        sx={{flexShrink: 0}}
                                    />
                                </Stack>
                            }
                            componentsProps={{typography: {sx: {flexGrow: 1, minWidth: 0}}}}
                        />
                        {index < filteredRecipes.length - 1 && <Divider/>}
                    </Box>;
                })}
                {!filteredRecipes.length && <Typography color="text.secondary" sx={{p: 2}}>{t('recipes:recipes_not_found')}</Typography>}
            </Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{mt: 3, mb: 1}}>
                <Box><Typography variant="h6">{t('recipes:recommended_prices')}</Typography><Typography variant="body2" color="text.secondary">{t('recipes:product_type_prices_hint')}</Typography></Box>
                <WriteButton disabled={RECIPE_SIZE.every(value => activeTiers.some(tier => Number(tier.fromValue) === Number(value)))} startIcon={<Add/>} variant="outlined" onClick={() => setPriceTiers(current => [...current, {fromValue: Number(RECIPE_SIZE.find(value => !activeTiers.some(tier => Number(tier.fromValue) === Number(value))) ?? RECIPE_SIZE[0]), price: 0}])}>{t('recipes:add_price')}</WriteButton>
            </Stack>
            <Stack spacing={1}>
                {priceTiers.map((tier, index) => {
                    const duplicate = activeTiers.filter(candidate => Number(candidate.fromValue) === Number(tier.fromValue)).length > 1;
                    return <Card key={tier.id ?? `new-${index}`} variant="outlined"><CardContent><Stack direction="row" spacing={1} alignItems="flex-start">
                        <FormControl fullWidth error={duplicate}>
                            <InputLabel>{t('recipes:from_proportion')}</InputLabel>
                            <Select label={t('recipes:from_proportion')} value={String(tier.fromValue)} onChange={event => updateTier(index, {fromValue: Number(event.target.value)})}>
                                {Array.from(new Set([...RECIPE_SIZE, String(tier.fromValue)])).sort((a, b) => Number(a) - Number(b)).map(value => <MenuItem key={value} value={value} disabled={activeTiers.some((candidate, candidateIndex) => candidateIndex !== index && Number(candidate.fromValue) === Number(value))}>{value}</MenuItem>)}
                            </Select>
                            {duplicate && <Typography variant="caption" color="error" sx={{mx: 1.75, mt: 0.5}}>{t('recipes:validation_duplicate_value')}</Typography>}
                        </FormControl>
                        <MoneyField fullWidth label={t('recipes:recommended_price')} value={tier.price} inputProps={{min: 0, step: 'any'}} error={Number(tier.price) < 0} onValueChange={price => updateTier(index, {price: price ?? NaN})}/>
                        <WriteIconButton color="error" onClick={() => removeTier(index)}><DeleteOutline/></WriteIconButton>
                    </Stack></CardContent></Card>;
                })}
            </Stack>
            <Stack direction="row" justifyContent="flex-end" sx={{mt: 2}}>
                <WriteButton variant="contained" onClick={() => void save()} disabled={isSaving || invalidTiers}>{t('common:save')}</WriteButton>
            </Stack>
        </Box>
    );
};
